import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useDealDetail } from "src/dm/hooks/useDeals";
import { useDealErrors } from "src/dm/hooks/useDealActivity";
import { useChangeStatus } from "src/dm/hooks/useDealActions";
import { DealErrorEntry } from "src/dm/interfaces/DealActivity";
import * as s from "./WarningsAndErrors.css";

/**
 * SPA port of legacy `WarningsAndErrors.aspx`. Surfaces validation rows
 * written to `DEAL_ERROR` by `dm0015_main` + the C# validator suite and
 * lets the user either fix the issues (via the deep-link in each row)
 * or — when all rows are warnings — Continue past them by re-running
 * the workflow change-status with `ignoreWarnings=true`.
 *
 * Legacy file: `Applications/Web/WarningsAndErrors.aspx.cs`. Audit ref:
 * 14-parity-matrix.md §5 item 17, audit 11 §7.
 */

// Severity translation: legacy `GetErrorType("W") → "WARNING" / else
// "ERROR"`. Render as a coloured pill plus the human label.
const severityLabel = (sev: string | null | undefined): "Error" | "Warning" => {
  const code = (sev ?? "").toUpperCase();
  return code === "W" ? "Warning" : "Error";
};

/**
 * RefLnk builder — mirrors the legacy `BuildLinks` helper. The legacy
 * code switches on `ditmId`:
 *
 * - `-1`  → Fees/ViewFees.aspx (fees-related rule)
 * - `0`   → DealHeader / NewDeal (header-level rule)
 * - `> 0` → Item/ViewItem.aspx?itemId={ditmId} (item-level rule)
 *
 * On top of that, ACTION_EVENT can promote a header row into a more
 * specific destination (e.g. notes / rewards) — those secondary
 * mappings are best-effort and fall back to the deal header.
 */
const buildReference = (
  dealId: number,
  row: DealErrorEntry
): { label: string; to: string } => {
  const ditmId = row.dealItemId ?? 0;
  const action = (row.actionEvent ?? "").toLowerCase();

  if (ditmId > 0) {
    return { label: `Item ${ditmId}`, to: `/dm/deals/${dealId}/items/${ditmId}` };
  }
  if (ditmId === -1) {
    return { label: "Fees", to: `/dm/deals/${dealId}/fees` };
  }
  // ditmId === 0 (or null) — header-level rule. ACTION_EVENT keywords
  // route to the more specific sub-page when present.
  if (action.includes("fee")) {
    return { label: "Fees", to: `/dm/deals/${dealId}/fees` };
  }
  if (action.includes("reward")) {
    return { label: "Reward", to: `/dm/deals/${dealId}/reward` };
  }
  if (action.includes("note")) {
    return { label: "Notes", to: `/dm/deals/${dealId}/notes` };
  }
  if (action.includes("promo") || action.includes("group")) {
    return { label: "Promo Groups", to: `/dm/deals/${dealId}/promo-groups` };
  }
  if (action.includes("attach")) {
    return { label: "Attachments", to: `/dm/deals/${dealId}/attachments` };
  }
  // Legacy default: NewDeal/DealHeader with mode=Edit. We always link
  // to the SPA edit page; the role layer (when present) will redirect
  // to read-only if needed.
  return { label: "Deal", to: `/dm/deals/${dealId}/edit` };
};

const WarningsAndErrorsPage = () => {
  const { dealId: dealIdParam } = useParams<{ dealId: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : 0;
  const navigate = useNavigate();
  const detail = useDealDetail(dealId || undefined);
  const errors = useDealErrors(dealId || undefined);
  const changeStatus = useChangeStatus(dealId);

  // Legacy `chkDisplay` — UNCHECKED by default; shows warnings (severity
  // = "W") in addition to errors when checked. The default matches the
  // legacy aspx initial state where the warnings filter is off.
  const [showWarnings, setShowWarnings] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Stable reference for `rows` so dependent useMemos don't recompute on
  // every render (react-hooks/exhaustive-deps).
  const rows = useMemo(() => errors.data ?? [], [errors.data]);
  const errorCount = useMemo(
    () => rows.filter((r) => (r.severity ?? "").toUpperCase() !== "W").length,
    [rows]
  );
  const warningCount = rows.length - errorCount;

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const sev = (r.severity ?? "").toUpperCase();
      if (sev === "W") return showWarnings;
      return true;
    });
  }, [rows, showWarnings]);

  // Legacy `showPopUp` + `btnClickContinue` flow: the Continue button is
  // ONLY enabled when every row on the deal is a warning (no hard
  // errors). Pushing past hard errors would corrupt the deal state.
  const canContinue = errorCount === 0 && warningCount > 0;

  const SeverityCell = (props: GridCellProps) => {
    const row = props.dataItem as DealErrorEntry;
    const label = severityLabel(row.severity);
    return (
      <td>
        <span className={label === "Error" ? s.sevError : s.sevWarning}>{label}</span>
      </td>
    );
  };

  const ReferenceCell = (props: GridCellProps) => {
    const row = props.dataItem as DealErrorEntry;
    const ref = buildReference(dealId, row);
    return (
      <td>
        <Link to={ref.to} className={s.refLink}>
          {ref.label} &rarr;
        </Link>
      </td>
    );
  };

  // POST .../status?force=true with target = the deal's *next* status.
  // Legacy WarningsAndErrors.aspx pulls the target status off the
  // query string; the SPA infers it from current state instead so the
  // user doesn't have to encode it.
  const handleContinue = async () => {
    if (!detail.data) return;
    const next = nextStatusFor(detail.data.status);
    try {
      await changeStatus.mutateAsync({ status: next, force: true });
      setConfirming(false);
      navigate(`/dm/deals/${dealId}`);
    } catch (e) {
      // Server-side workflow can still reject (e.g. role check). Bubble
      // the message back into the modal instead of throwing.
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      alert(err?.response?.data?.message ?? err?.message ?? "Continue failed.");
    }
  };

  if (!dealId || Number.isNaN(dealId)) return <p>Invalid deal id.</p>;
  if (errors.isLoading || detail.isLoading) return <p>Loading errors&hellip;</p>;
  if (errors.isError) {
    return (
      <p style={{ color: "#b91c1c" }}>
        {errors.error instanceof Error ? errors.error.message : "Failed to load"}
      </p>
    );
  }

  return (
    <div className={s.wrap}>
      <div className={s.headerBar}>
        <h2 className={s.title}>Warnings &amp; Errors</h2>
        <div className={s.actions}>
          <Checkbox
            label="Display Warnings"
            checked={showWarnings}
            onChange={(e) => setShowWarnings(!!e.value)}
          />
          <Button
            type="button"
            themeColor="primary"
            disabled={!canContinue || changeStatus.isPending}
            onClick={() => setConfirming(true)}
            title={
              canContinue
                ? "Ignore these warnings and continue the workflow transition"
                : errorCount > 0
                ? "Resolve hard errors before continuing"
                : "Nothing to continue past"
            }
          >
            {changeStatus.isPending ? "Continuing…" : "Continue"}
          </Button>
        </div>
      </div>

      <p className={s.meta}>
        {errorCount} error{errorCount === 1 ? "" : "s"} &middot; {warningCount} warning
        {warningCount === 1 ? "" : "s"} &middot; showing {filtered.length}
      </p>

      {filtered.length === 0 ? (
        <p style={{ color: "#94a3b8" }}>
          {rows.length === 0
            ? "No validation messages on this deal."
            : "No rows match the current filter. Enable “Display Warnings” to see warning-severity rows."}
        </p>
      ) : (
        <Grid data={filtered}>
          <GridColumn field="errorId" title="#" width="80px" />
          <GridColumn title="Severity" width="110px" cells={{ data: SeverityCell }} />
          <GridColumn field="dealItemId" title="Item" width="100px" />
          <GridColumn field="actionEvent" title="Event" width="180px" />
          <GridColumn field="message" title="Message" />
          <GridColumn title="Reference" width="160px" cells={{ data: ReferenceCell }} />
        </Grid>
      )}

      {confirming && (
        <Dialog
          title="Continue past warnings?"
          width={520}
          onClose={() => setConfirming(false)}
        >
          <p style={{ marginTop: 0 }}>
            Are you sure you want to ignore these warnings and proceed?
          </p>
          <p style={{ fontSize: "0.85rem", color: "#475569" }}>
            {warningCount} warning{warningCount === 1 ? "" : "s"} will be left in
            place. The deal will transition to{" "}
            <strong>{nextStatusFor(detail.data?.status)}</strong> without re-running
            validation.
          </p>
          <DialogActionsBar>
            <Button onClick={() => setConfirming(false)} disabled={changeStatus.isPending}>
              Cancel
            </Button>
            <Button
              themeColor="primary"
              onClick={handleContinue}
              disabled={changeStatus.isPending}
            >
              {changeStatus.isPending ? "Working…" : "Continue"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

/**
 * Mirror of the server-side NextStatusFor switch in DealsController.cs.
 * Kept client-side ONLY so the confirm-modal can display the destination
 * status to the user; the server makes the final decision when the
 * status change actually runs.
 */
const nextStatusFor = (current?: string | null): string => {
  switch ((current ?? "NEW").toUpperCase()) {
    case "NEW":
    case "PREPARE":
    case "MODIFY":
    case "UNLOCK":
      return "SUBMIT";
    case "REJECT":
      return "RESUBMIT";
    case "RESUBMIT":
    case "SUBMIT":
      return "REVIEW";
    case "REVIEW":
      return "APPROVE";
    case "APPROVE":
      return "AUTHORIZE";
    case "AUTHORIZE":
      return "START";
    case "START":
      return "COMPLETE";
    default:
      return (current ?? "NEW").toUpperCase();
  }
};

export default WarningsAndErrorsPage;
